import type { GeneratedMedia } from '~entities/media';
import { saveToSessionStorage, ssKeys } from '~features/storage';
import { serializeQueryArgs } from '~services/utils';
import { getPaginationParams } from '~services/utils/pagination';
import { getPaginationByPageParams } from '~services/utils/paginationByPage';
import { api, apiTags, prepareMedia } from '~shared/api/origin';
import { TokenManager } from '~shared/services/ClientTokenManager';
import indexDb from '~utils/indexDb';

import type {
	ActivateGeneratedBotApiResponse,
	BotWizardApiGenerateBotPrompt,
	BotWizardApiResponse,
	GenerateImagesApiRequest,
	GenerateImagesApiResponse,
	GenerateMediaApiResponse,
	GenerateVideoFromImageApiResponse,
	GenerateVideosApiRequest,
	GenerateVideosApiResponse,
	GetGenerationBotQuotaApiResponse,
	GetGenerationImagesQuotaApiResponse,
	GetGenerationImagesSettingsApiResponse,
	GetGenerationMediaBotsApiResponse,
	GetGenerationVideosQuotaApiResponse,
	GetGenerationVideosSettingsApiResponse,
} from './types';

export const {
	util,
	// character
	useGetBotWizardSettingsQuery,
	useGeneratePromptMutation,
	useGetGenerationBotQuotaQuery,
	useActivateGeneratedBotMutation,
	useGenerateBotPromptMutation,
	useGenerateBotPresetMutation,
	// media
	useGetGeneratedMediaQuery,
	useGetGeneratedPublicMediaQuery,
	useGetGeneratedSimilarMediaQuery,
	useGetGeneratedLikedMediaQuery,
	useGetGeneratedUnlockedMediaQuery,
	useImprovePromptMutation,
	useDeleteGeneratedMediaMutation,
	// images
	useGetGenerationImagesSettingsQuery,
	useGetGenerationImagesBotsQuery,
	useGetGenerationImagesQuotaQuery,
	useGenerateImagesMutation,
	// videos
	useGetGenerationVideosSettingsQuery,
	useGetGenerationVideosBotsQuery,
	useGetGenerationVideosQuotaQuery,
	useGenerateVideosMutation,
	useGenerateVideoFromImageMutation,
	useGenerateVideoFromMessageImageMutation,
	// query state
	endpoints: {
		getGeneratedMedia: { useQueryState: useGetGeneratedMediaQueryState },
	},
} = api.injectEndpoints({
	endpoints: (build) => ({
		// character
		getBotWizardSettings: build.query<BotWizardApiResponse, void>({
			// @ts-ignore
			queryFn: async (args, queryApi, extraOptions, baseQuery) => {
				const key = `${serializeQueryArgs('getBotWizardSettingsV2', {})}`;
				const cachedData = (await indexDb.getCache(
					key
				)) as BotWizardApiResponse;
				if (cachedData) {
					queryApi.dispatch(
						util.upsertQueryData('getBotWizardSettings', undefined, cachedData)
					);
				}

				try {
					const response = await baseQuery({
						url: `/ai/v2/generation/bot/settings`,
					});

					if (response.data) {
						void indexDb.putCache(key, response.data);
					}

					return response;
				} catch (error) {
					return { error };
				}
			},
		}),
		generatePrompt: build.mutation<
			{ appearance: string; description: string },
			{ name: string; gender: string; userId: string }
		>({
			query: ({ userId, name, gender }) => ({
				url: `/ai/v2/generation/bot/${userId}/prompt`,
				method: 'POST',
				body: {
					name,
					gender,
				},
			}),
		}),

		generateBotPrompt: build.mutation<
			BotWizardApiGenerateBotPrompt,
			{
				userId: string;
				name: string;
				style: string;
				gender: string;
				appearance: string;
				description: string;
			}
		>({
			query: ({ userId, name, gender, style, appearance, description }) => ({
				url: `/ai/v2/generation/${userId}/prompt/bot`,
				method: 'PUT',
				body: {
					data: {
						name,
						gender,
						style,
						appearance,
						description,
					},
				},
			}),
		}),

		generateBotPreset: build.mutation<
			BotWizardApiGenerateBotPrompt,
			{
				userId: string;
				name: string;
				style: string;
				gender: string;
				age: number;
				height: string;
				physique: string;
				boobs?: string;
				butt?: string;
				penisSize?: string;
				hairStyle: string;
				ethnic: string;
				personality: { [key: string]: number };
				hairColor: string;
				eyeColor: string;
				orientation: string;
				chatStyle: string;
				occupation?: string;
				startingRelationship?: string;
				kinks: string[];
			}
		>({
			query: ({
				userId,
				name,
				gender,
				ethnic,
				style,
				chatStyle,
				age,
				boobs,
				butt,
				hairStyle,
				hairColor,
				eyeColor,
				height,
				kinks,
				occupation,
				orientation,
				penisSize,
				physique,
				personality,
				startingRelationship,
			}) => ({
				url: `/ai/v2/generation/${userId}/guided/bot`,
				method: 'PUT',
				body: {
					data: {
						name,
						gender,
						style,
						ethnic,
						chatStyle,
						age,
						boobs,
						butt,
						hairStyle,
						hairColor,
						eyeColor,
						height,
						kinks,
						occupation,
						orientation,
						penisSize,
						physique,
						startingRelationship,
						personality,
					},
				},
			}),
		}),

		getGenerationBotQuota: build.query<
			GetGenerationBotQuotaApiResponse,
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/generation/${userId}/bot/quota`,
			}),
			providesTags: [apiTags.characterGenerationQuota],
		}),

		activateGeneratedBot: build.mutation<
			ActivateGeneratedBotApiResponse,
			{
				userId: string;
				botId: number;
				isPublic: boolean;
				selectedAvatarIndex: number;
			}
		>({
			query: ({ userId, botId, isPublic, selectedAvatarIndex }) => ({
				url: `ai/generation/${userId}/bot/${botId}/activate`,
				method: 'POST',
				body: {
					access: isPublic ? 'public' : 'private',
					selectedAvatarIndex,
				},
			}),
			async onQueryStarted({ isPublic }, { queryFulfilled, dispatch }) {
				try {
					await queryFulfilled;
					if (isPublic) {
						dispatch(api.util.invalidateTags([apiTags.grid]));
					}
				} catch {}
			},
		}),

		// media
		getGeneratedMedia: build.query<
			GenerateMediaApiResponse,
			{ userId: string; omit: number; select: number }
		>({
			query: ({ userId, omit, select }) => ({
				url: `/ai/generation/v2/${userId}/media`,
				params: { omit, select },
			}),
			...getPaginationParams('media', prepareMedia, false),
			providesTags: [apiTags.generatedMedia],
		}),

		getGeneratedSimilarMedia: build.query<
			GenerateMediaApiResponse,
			{ userId: string; parentMediaId: string; omit: number; select: number }
		>({
			query: ({ userId, parentMediaId, omit, select }) => ({
				url: `/ai/generation/${userId}/media/search/similar/${parentMediaId}`,
				params: { omit, select },
			}),
			...getPaginationParams('media', prepareMedia),
			providesTags: [apiTags.generatedSimilarMedia],
		}),

		// Формально это api/media, а не api/generation,
		// но getGeneratedMedia и getGeneratedPublicMedia аналогичны,
		// поэтому getGeneratedLikedMedia тоже лежит здесь
		getGeneratedLikedMedia: build.query<
			GenerateMediaApiResponse,
			{ userId: string; page: number }
		>({
			query: ({ userId, page }) => ({
				url: `/ai/media/${userId}/liked`,
				params: { page },
			}),
			...getPaginationByPageParams('media', prepareMedia),
			providesTags: [apiTags.generatedLikedMedia],
		}),

		getGeneratedUnlockedMedia: build.query<
			GenerateMediaApiResponse,
			{ userId: string; page: number }
		>({
			query: ({ userId, page }) => ({
				url: `/ai/media/${userId}/unlocked`,
				params: { page },
			}),
			...getPaginationByPageParams('media', prepareMedia),
			providesTags: [apiTags.generatedUnlockedMedia],
		}),

		getGeneratedPublicMedia: build.query<
			GenerateMediaApiResponse,
			{ userId: string; omit: number; select: number }
		>({
			query: ({ userId, omit, select }) => ({
				url: `/ai/generation/${userId}/media/public`,
				params: { omit, select },
			}),
			...getPaginationParams('media', prepareMedia, false),
			providesTags: [apiTags.generatedPublicMedia],
		}),

		deleteGeneratedMedia: build.mutation<
			undefined,
			{ userId: string; media: GeneratedMedia }
		>({
			query: ({ userId, media }) => ({
				url: `ai/generation/${userId}/${media.type}/${media.upstreamId}`,
				method: 'DELETE',
			}),
		}),

		improvePrompt: build.mutation<
			{ prompt: string },
			{ prompt: string; userId: string }
		>({
			query: ({ prompt, userId }) => ({
				url: `ai/generation/${userId}/prompt/improve`,
				method: 'POST',
				body: { prompt },
			}),
		}),

		// images
		getGenerationImagesSettings: build.query<
			GetGenerationImagesSettingsApiResponse,
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/generation/${userId}/images/settings`,
			}),
			transformResponse: (
				data: GetGenerationImagesSettingsApiResponse
			): GetGenerationImagesSettingsApiResponse => ({
				...data,
				actions: data.actions || [],
				poses: data.poses || [],
			}),
		}),

		getGenerationImagesBots: build.query<
			GetGenerationMediaBotsApiResponse['bots'],
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/generation/${userId}/images/bots`,
			}),
			transformResponse: (
				data: GetGenerationMediaBotsApiResponse
			): GetGenerationMediaBotsApiResponse['bots'] => {
				return data.bots;
			},
			providesTags: [apiTags.imagesGenerationBots],
		}),

		getGenerationImagesQuota: build.query<
			GetGenerationImagesQuotaApiResponse,
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/generation/${userId}/images/quota`,
			}),
			providesTags: [apiTags.imagesGenerationQuota],
		}),

		generateImages: build.mutation<
			GenerateImagesApiResponse,
			{ userId: string; body: GenerateImagesApiRequest }
		>({
			query: ({ userId, body }) => ({
				url: `ai/generation/${userId}/image`,
				method: 'PUT',
				body,
			}),
			async onQueryStarted({ userId, body }, { dispatch, queryFulfilled }) {
				saveToSessionStorage(ssKeys.G_TOOL_GENERATE_IN_PROGRESS, true);
				dispatch(
					util.updateQueryData(
						'getGenerationImagesQuota',
						{ userId },
						(draft) => {
							const { type } = TokenManager.getInfo();
							if (!body.botId) {
								if (type === 'demo') {
									draft.quotas.free =
										body.imageCount > draft.quotas.demo
											? 0
											: draft.quotas.demo - body.imageCount;
								}
								if (draft.quotas.free) {
									draft.quotas.free =
										body.imageCount > draft.quotas.free
											? 0
											: draft.quotas.free - body.imageCount;
								} else {
									draft.quotas.monthly =
										body.imageCount > draft.quotas.monthly
											? 0
											: draft.quotas.monthly - body.imageCount;
								}
							}
						}
					)
				);

				try {
					const { data: res } = await queryFulfilled;
					dispatch(
						util.updateQueryData(
							'getGeneratedMedia',
							{ userId, omit: NaN, select: NaN },
							(draft) => {
								const images = prepareMedia(
									res.images.filter(
										({ id }) => !draft.media.find((it) => it.id === id)
									)
								);

								draft.media.unshift(...images);
								draft.totalLength += images.length;
							}
						)
					);
				} catch {}
			},
			invalidatesTags: (_, __, args) => {
				const tags = [apiTags.imagesGenerationQuota];
				if (args.body.additionalOptions?.isPublic) {
					tags.push(apiTags.generatedPublicMedia);
				}
				return tags;
			},
		}),

		// videos
		getGenerationVideosSettings: build.query<
			GetGenerationVideosSettingsApiResponse,
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/generation/${userId}/videos/settings`,
			}),
			transformResponse: (
				data: GetGenerationVideosSettingsApiResponse
			): GetGenerationVideosSettingsApiResponse => ({
				...data,
				actions: data.actions || [],
			}),
		}),

		getGenerationVideosQuota: build.query<
			GetGenerationVideosQuotaApiResponse,
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/generation/${userId}/videos/quota`,
			}),
			providesTags: [apiTags.videosGenerationQuota],
		}),

		getGenerationVideosBots: build.query<
			GetGenerationMediaBotsApiResponse['bots'],
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/generation/${userId}/videos/bots`,
			}),
			transformResponse: (
				data: GetGenerationMediaBotsApiResponse
			): GetGenerationMediaBotsApiResponse['bots'] => {
				return data.bots;
			},
			providesTags: [apiTags.videosGenerationBots],
		}),

		generateVideos: build.mutation<
			GenerateVideosApiResponse,
			{ userId: string; body: GenerateVideosApiRequest }
		>({
			query: ({ userId, body }) => ({
				url: `ai/generation/${userId}/video`,
				method: 'PUT',
				body,
			}),
			async onQueryStarted({ userId, body }, { dispatch, queryFulfilled }) {
				saveToSessionStorage(ssKeys.G_TOOL_GENERATE_IN_PROGRESS, true);
				dispatch(
					util.updateQueryData(
						'getGenerationVideosQuota',
						{ userId },
						(draft) => {
							if (!body.botId) {
								draft.quotas.monthly = Math.max(0, draft.quotas.monthly - 1);
							}
						}
					)
				);

				try {
					const { data: res } = await queryFulfilled;
					dispatch(
						util.updateQueryData(
							'getGeneratedMedia',
							{ userId, omit: NaN, select: NaN },
							(draft) => {
								const videos = prepareMedia(
									res.videos.filter(
										({ id }) => !draft.media.find((it) => it.id === id)
									)
								);

								draft.media.unshift(...videos);
								draft.totalLength += videos.length;
							}
						)
					);
				} catch {}
			},
			invalidatesTags: (_, __, args) => {
				const tags = [apiTags.videosGenerationQuota];
				if (args.body.additionalOptions?.isPublic) {
					tags.push(apiTags.generatedPublicMedia);
				}
				return tags;
			},
		}),

		generateVideoFromImage: build.mutation<
			GenerateVideoFromImageApiResponse,
			{ userId: string; imageGenerationId: string }
		>({
			query: ({ userId, imageGenerationId }) => ({
				url: `/ai/generation/v2/${userId}/video/from/image/${imageGenerationId}`,
				method: 'POST',
			}),
			async onQueryStarted({ userId }, { dispatch, queryFulfilled }) {
				saveToSessionStorage(ssKeys.G_TOOL_GENERATE_IN_PROGRESS, true);
				dispatch(
					util.updateQueryData(
						'getGenerationVideosQuota',
						{ userId },
						(draft) => {
							draft.quotas.monthly = Math.max(0, draft.quotas.monthly - 1);
						}
					)
				);

				try {
					const { data: res } = await queryFulfilled;
					dispatch(
						util.updateQueryData(
							'getGeneratedMedia',
							{ userId, omit: NaN, select: NaN },
							(draft) => {
								const videos = prepareMedia(
									res.videos.filter(
										({ id }) => !draft.media.find((it) => it.id === id)
									)
								);

								draft.media.unshift(...videos);
								draft.totalLength += videos.length;
							}
						)
					);
				} catch {}
			},
			invalidatesTags: [apiTags.videosGenerationQuota],
		}),

		generateVideoFromMessageImage: build.mutation<
			GenerateVideoFromImageApiResponse,
			{ userId: string; botId: string; messageId: number }
		>({
			query: ({ userId, botId, messageId }) => ({
				url: `/ai/generation/${userId}/video/from/${botId}/message/${messageId}`,
				method: 'POST',
			}),
		}),
	}),
});
